import { Filter, filters, Renderer, RenderTexture, Sprite, Texture } from "pixi.js"
import { StoreyFocusFilter } from "./storey_focus_filter"

/**
 * Compile filter shaders before they are first needed. WebGL links a filter's program the first
 * time the filter draws, and that is a main-thread stall: 36ms for the occlusion outline on a fast
 * Mac, more on a modest machine. Its first use was landing behind a building after a fall. Programs
 * are shared by every filter with the same source, so one draw of each kind is enough.
 *
 * The filters drawn here are never destroyed: the renderer keeps a reference to the last shader it
 * bound, and a destroyed one broke the next render ("Cannot read properties of null (reading
 * 'uniforms')"). They, the probe and the 2x2 target are a few bytes, kept for the session.
 */
const warmed: { filters: Filter[]; target?: RenderTexture; probe?: Sprite } = { filters: [] }

export const warmFilterShaders = (renderer: Renderer, owned: Filter[] = []): void => {
    // The kinds whose first use comes mid-game: a roof fading (sky), and passive floors' blur and shade.
    // A focus blur wide enough to halve draws with all three of its programs.
    const kinds = [new filters.AlphaFilter(0.5), new filters.BlurFilter(0.75, 2, 0.5), new filters.ColorMatrixFilter(),
        Object.assign(new StoreyFocusFilter(), { sigma: 4 })]
    warmed.filters.push(...kinds)
    warmed.target ||= RenderTexture.create({ width: 2, height: 2 })
    const probe = warmed.probe ||= new Sprite(Texture.WHITE)
    probe.width = probe.height = 2
    try {
        for (const filter of [...owned, ...kinds]) {
            probe.filters = [filter]
            renderer.render(probe, { renderTexture: warmed.target, clear: true })
        }
    } catch {
        // No usable renderer (headless, or a lost context): they compile on first use instead.
    } finally {
        probe.filters = null
    }
}
